import { createRouter, createWebHistory } from 'vue-router'
import LoginView from '../views/LoginView.vue'
import DeviceSelectView from '../views/DeviceSelectView.vue'
import DashboardView from '../views/DashboardView.vue'
import AdminView from '../views/AdminView.vue'
import i18n from '../i18n'

/**
 * Vue Router 配置
 * Phase 2.4: 完整的四頁面流程
 * 1. Login (登入)
 * 2. Device Selection (設備選擇)
 * 3. Dashboard (即時儀表板)
 * 4. Admin (管理員頁面) - 僅管理員帳號可訪問
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/login'
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: {
        title: 'titles.login',
        requiresAuth: false
      }
    },
    {
      path: '/devices',
      name: 'devices',
      component: DeviceSelectView,
      meta: {
        title: 'titles.devices',
        requiresAuth: true
      }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardView,
      meta: {
        title: 'titles.dashboard',
        requiresAuth: true
      }
    },
    {
      path: '/coating',
      name: 'coating',
      component: () => import('../views/CoatingView.vue'),
      meta: {
        title: 'titles.coating',
        requiresAuth: true
      }
    },
    {
      path: '/roi-calibration',
      name: 'roi-calibration',
      component: () => import('../views/RoiCalibrationView.vue'),
      meta: {
        title: 'titles.roi',
        requiresAuth: true,
        requiresAdmin: true // 標定影響全裝置數據, 僅管理員 (devices 含 '*')
      }
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminView,
      meta: {
        title: 'titles.admin',
        requiresAuth: true,
        requiresAdmin: true
      }
    }
  ]
})

/**
 * 路由守衛 (Authentication Guard)
 * 商用多租戶應用 - 每次都從登入頁開始
 *
 * 重要:
 * - 不會自動跳轉已登入用戶
 * - 每次訪問都需要重新登入（商用安全考量）
 * - 只檢查受保護頁面是否有 token
 * - 管理員頁面需要額外檢查管理員權限 (devices 包含 '*')
 */
router.beforeEach((to, _from, next) => {
  // 更新頁面標題
  if (to.meta.title) {
    // meta.title 存 i18n key, 依當前語系翻譯
    document.title = i18n.global.t(to.meta.title as string)
  }

  // 檢查是否需要認證
  const requiresAuth = to.meta.requiresAuth
  const requiresAdmin = to.meta.requiresAdmin
  const token = localStorage.getItem('token')

  if (requiresAuth && !token) {
    // 需要認證但沒有 token，跳轉到登入頁
    console.log('⚠️ 未登入，跳轉到登入頁')
    // 清除所有相關資料
    localStorage.removeItem('user')
    localStorage.removeItem('selectedDeviceId')
    next('/login')
    return
  }

  // 檢查管理員權限
  if (requiresAdmin) {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      // 管理員帳號的特徵: devices 陣列包含 '*'
      const isAdmin = Array.isArray(user.devices) && user.devices.includes('*')
      if (!isAdmin) {
        console.log('⚠️ 非管理員帳號，無法訪問管理頁面')
        alert(i18n.global.t('errors.adminOnly'))
        next('/devices')
        return
      }
    } else {
      console.log('⚠️ 用戶資料不存在，跳轉到登入頁')
      next('/login')
      return
    }
  }

  // 其他情況，允許訪問（包括已登入用戶訪問登入頁）
  next()
})

/**
 * 全局錯誤處理
 * 監聽 window 的 storage 事件，當 token 被移除時自動跳轉
 * 注意: storage 事件只在不同分頁間觸發，同一分頁需要定期檢查
 */
window.addEventListener('storage', (e) => {
  if (e.key === 'token' && !e.newValue) {
    // Token 被移除，跳轉到登入頁
    console.log('⚠️ Token 已失效（跨分頁），跳轉到登入頁')
    router.push('/login')
  }
})

/**
 * 定期檢查 Token 是否存在
 * 每 3 秒檢查一次，如果在受保護頁面但沒有 token，自動跳轉登入頁
 */
setInterval(() => {
  const currentPath = router.currentRoute.value.path
  const requiresAuth = router.currentRoute.value.meta.requiresAuth
  const token = localStorage.getItem('token')

  if (requiresAuth && !token && currentPath !== '/login') {
    console.log('⚠️ Token 已失效（定期檢查），跳轉到登入頁')
    localStorage.removeItem('user')
    localStorage.removeItem('selectedDeviceId')
    router.push('/login')
  }
}, 3000) // 每 3 秒檢查一次

export default router
